// ThemePrefsSync.js

// bridge/ThemePrefsSync.jsx
import { useEffect } from "react";
import { useTheme } from "../providers/ThemeProvider";
import { useSettingsRepo } from "../features/settings/db/useSettingsRepo"; // or adjust import

const THEME_MODE_KEY = "theme-mode";
const THEME_ACCENT_KEY = "theme-accent";

// tiny helper: safely call a possibly-missing async function
const safeCall = async (fn, ...args) => {
  try {
    if (typeof fn === "function") return await fn(...args);
  } catch {
    // swallow — persistence must never crash the app
  }
  return undefined;
};

/**
 * ThemePrefsSync
 *
 * Bridge between the ThemeProvider (via useTheme()) and a persistent settings repo
 * (via useSettingsRepo(), e.g. a Dexie-backed repository). This component does not
 * render anything (returns null) — it only synchronizes theme preferences in both
 * directions:
 *
 * - Hydration: on mount (and when `repo` or the setter functions change), it reads
 *   persisted values from the repo and updates the ThemeProvider using setThemeMode
 *   and setThemeAccent.
 * - Persistence: whenever the provider's `themeMode` or `themeAccent` changes, it
 *   writes the current values back to the repo.
 *
 * Implementation notes
 * - Hydration effect:
 *   - Declared first. Runs after render when dependencies change.
 *   - Uses `safeCall(repo?.get, 'theme-mode')` and `safeCall(repo?.get, 'theme-accent')`
 *     in parallel to fetch stored values.
 *   - Uses an `alive` flag to ignore late async results if the component unmounts.
 *   - Validates values before applying:
 *       * theme mode must be "light", "dark" or "auto" to call setThemeMode.
 *       * theme accent must be a non-empty string to call setThemeAccent.
 *   - Because the read is async, the provider may initially keep its own defaults
 *     until the repo values are applied.
 *
 * - Persistence effects:
 *   - Two effects declared after the hydration effect:
 *       1) persist `themeMode` using `safeCall(repo.set, THEME_MODE_KEY, themeMode ?? "auto")`
 *       2) persist `themeAccent` using `safeCall(repo.set, THEME_ACCENT_KEY, themeAccent ?? "default")`
 *   - Each effect is guarded by `if (!repo) return;` so nothing is written until the repo exists.
 *   - They run after render whenever their respective dependency (`themeMode` or `themeAccent`)
 *     changes. Because effects are invoked in declaration order, the hydration effect is scheduled
 *     first; however, the hydration read is asynchronous, so a persistence effect may write the
 *     provider's initial/default value to storage before hydration completes. When hydration later
 *     calls the setters, those setter calls will update the provider and may trigger persistence again.
 *
 * - Error handling
 *   - All repo operations are wrapped in `safeCall` to swallow/recover from errors.
 *
 * Side effects and lifecycle summary (order of invocation)
 * 1. Component renders using ThemeProvider values (initial/defaults).
 * 2. React schedules effects in declaration order:
 *    a. Hydration effect (starts an async read).
 *    b. Persist themeMode effect (runs if `repo` exists and may write current themeMode).
 *    c. Persist themeAccent effect (runs if `repo` exists and may write current themeAccent).
 * 3. Hydration async read resolves later and, if still mounted, updates the provider via setters.
 * 4. Provider updates may retrigger the persistence effects, causing stored values to be updated.
 *
 * Returns: null (no UI)
 *
 * @returns {null}
 */
export default function ThemePrefsSync() {
  const repo = useSettingsRepo(); // requires DexieProvider above
  const { themeMode, themeAccent, setThemeMode, setThemeAccent } = useTheme();

  // (hydrate)
  useEffect(() => {
    let alive = true;
    (async () => {
      const [m, a] = await Promise.all([
        safeCall(repo?.get, "theme-mode"),
        safeCall(repo?.get, "theme-accent"),
      ]);
      if (!alive) return;
      if (m === "light" || m === "dark" || m === "auto") setThemeMode(m);
      if (typeof a === "string" && a.trim().length > 0) setThemeAccent(a);
      // else: do nothing, keep the provider’s default
    })();
    return () => {
      alive = false;
    };
  }, [repo, setThemeMode, setThemeAccent]);

  // Persist on changes (ignore until repo exists)
  useEffect(() => {
    if (!repo) return;
    void safeCall(repo.set, THEME_MODE_KEY, themeMode ?? "auto");
  }, [repo, themeMode]);

  useEffect(() => {
    if (!repo) return;
    void safeCall(repo.set, THEME_ACCENT_KEY, themeAccent ?? "default");
  }, [repo, themeAccent]);

  // useEffect(() => {
  //   if (!repo) return;
  //   void safeCall(repo.put, THEME_MODE_KEY, themeAccent ?? "default"); // <- never store undefined
  // }, [repo, themeAccent]);

  // previous
  // Hydrate once from Dexie
  // useEffect(() => {
  //   let alive = true;
  //   (async () => {
  //     const [m, a] = await Promise.all([
  //       safeCall(repo?.get, THEME_MODE_KEY),
  //       safeCall(repo?.get, THEME_ACCENT_KEY),
  //     ]);
  //     if (!alive) return;
  //     console.log(m);
  //     if (m) setThemeMode(m);
  //     if (a) setThemeAccent(a);
  //   })();
  //   return () => {
  //     alive = false;
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [repo]);

  // // Persist on changes (ignore until repo exists)
  // useEffect(() => {
  //   console.log(themeMode);
  //   if (!repo) return;
  //   void safeCall(repo.put, THEME_MODE_KEY, themeMode);
  // }, [repo, themeMode]);

  // useEffect(() => {
  //   if (!repo) return;
  //   void safeCall(repo.put, THEME_ACCENT_KEY, themeAccent);
  // }, [repo, themeAccent]);

  return null;
}

//previous
// import { useEffect } from "react";
// import { useTheme } from "../providers/ThemeProvider";
// import { useSettingsRepo } from "../features/settings/db/useSettingsRepo"; // or adjust import

// const THEME_MODE_KEY = "theme-mode";
// const THEME_ACCENT_KEY = "theme-accent";

// export default function ThemeSync() {
//   const repo = useSettingsRepo(); // requires DexieProvider above
//   const { themeMode, themeAccent, setThemeMode, setThemeAccent } = useTheme();

//   // hydrate once
//   useEffect(() => {
//     let alive = true;
//     (async () => {
//       const [m, a] = await Promise.all([
//         repo.get(THEME_MODE_KEY),
//         repo.get(THEME_ACCENT_KEY),
//       ]);
//       if (!alive) return;
//       if (m) setThemeMode(m);
//       if (a) setThemeAccent(a);
//     })();
//     return () => {
//       alive = false;
//     };
//   }, [repo, setThemeMode, setThemeAccent]);

//   // persist on changes
//   useEffect(() => {
//     repo.set(THEME_MODE_KEY, themeMode);
//   }, [repo, themeMode]);
//   useEffect(() => {
//     repo.set(THEME_ACCENT_KEY, themeAccent);
//   }, [repo, themeAccent]);

//   return null;
// }
